import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import Portal from '../Portal/Portal';
import styles from './ConfirmationModal.module.scss';
import { useNavigate } from 'react-router-dom';
import modalExit from '../../../assets/icons/vector/exitModalIcon.svg';
import warningSvg from '../../../assets/icons/vector/warning-alert.svg';
import successSvg from '../../../assets/icons/vector/success-alert.svg';
import updateSvg from '../../../assets/icons/vector/update-alert.svg';

import Button from 'component/atom/forms/button/Button';
import { useBrandingContext } from 'app-context/BrandingsContext';
import SvgWithBranding from '../branding/withBrandingColor.hoc';
import Input from 'component/atom/forms/input/input/Input';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onSubmit,
  route,
  closeOnSubmit,
  children,
  onApproveButton,
  text,
  subText,
  isSuccess,
  isUpdate,
  timeout,
  isLoading,
  type,
  handleCancel,
  subTextClassName,
  modalContentClassName,
  buttonClassName,
  closeBtnContainerClassName,
  closeBtnClassName,
  subTextIsHtml = false,
  hasTypeName = false,
  confirmText,
  setConfirmText,
}) => {
  const navigate = useNavigate();
  const nodeRef = useRef(null);
  const { brandingData } = useBrandingContext();
  const { page_TextColor } = brandingData;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      if (timeout) {
        const timer = setTimeout(() => {
          handleClose();
        }, timeout);

        return () => clearTimeout(timer);
      }
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen, timeout]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
    if (route) {
      document.body.style.overflow = 'auto';
      navigate(route);
    }
    if (closeOnSubmit) handleClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        if (handleCancel) {
          handleCancel();
        } else {
          handleClose();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleCancel]);

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames={{
          enter: styles['fade-enter'],
          enterActive: styles['fade-enter-active'],
          exit: styles['fade-exit'],
          exitActive: styles['fade-exit-active'],
        }}
        unmountOnExit
        nodeRef={nodeRef}
      >
        <div
          className={styles.modal}
          onClick={handleClose}
          ref={nodeRef}
          style={{
            color: page_TextColor,
          }}
        >
          <div
            className={`${styles.modalContent} ${modalContentClassName}`}
            onClick={(e) => e.stopPropagation()}
          >
            {onClose ? (
              <div
                className={`${styles.closeButton} ${closeBtnContainerClassName}`}
              >
                <SvgWithBranding
                  type='exit_modal'
                  onClick={handleClose}
                  className={`${styles['exit-img']} ${closeBtnClassName}`}
                />
              </div>
            ) : null}
            {isSuccess ? (
              <div className={styles['warning-img']}>
                <img src={successSvg} alt='Warning' />
              </div>
            ) : isUpdate ? (
              <div className={styles['warning-img']}>
                <img src={updateSvg} alt='Warning' />
              </div>
            ) : (
              <div className={styles['warning-img']}>
                <img src={warningSvg} alt='Warning' />
              </div>
            )}

            <div className={styles.textCont}>
              <p className={styles.title}>{text}</p>
              {subText != null &&
                (typeof subText === 'string' ? (
                  subTextIsHtml ? (
                    <p
                      className={styles.subText}
                      dangerouslySetInnerHTML={{ __html: subText }}
                    />
                  ) : (
                    <p className={styles.subText}>{subText}</p>
                  )
                ) : (
                  // JSX/ReactNode
                  <div className={styles.subText}>{subText}</div>
                ))}
              {hasTypeName && (
                <Input
                  placeholder='Enter Bot Name'
                  onChange={(e) => setConfirmText(e.target.value)}
                  value={confirmText}
                />
              )}
            </div>
            {children}
            {onSubmit ? (
              <div className={styles.actions}>
                <Button
                  label='Cancel'
                  onClick={handleCancel ? handleCancel : handleClose}
                  variant='dark'
                  className={`${buttonClassName} ${styles['cancel-btn']}`}
                />
                <Button
                  label={onApproveButton?.text}
                  variant={isUpdate ? '' : 'danger-rev'}
                  // type={appConstants.TYPE.THUMB_DOWN}
                  type={type ? type : null}
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  className={`${buttonClassName} ${styles['approve-btn']}`}
                />
              </div>
            ) : null}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};

export default ConfirmationModal;
